import React, { Component } from 'react';
import PropTypes from 'prop-types';

/**
 * Error boundary around the forms-core form. Without it, any throw inside
 * `<VulcanFormStepRunner>` bubbles to the app-root ErrorBoundary and
 * white-screens the entire page; this contains the failure to the form itself so
 * the surrounding app (header, nav, status bar) stays usable.
 *
 * Error DETAIL (message + component stack) renders inline only OUTSIDE
 * production — it made real-data debugging fast while this was behind a flag.
 * Now that the engine is the default path this is user-facing, so production
 * shows a generic message and the detail goes to the console instead.
 */
const isDev = process.env.NODE_ENV !== 'production';
class FormsCorePreviewBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Always logged, in every environment — production users get the generic
    // message above, but the detail must remain recoverable.
    // eslint-disable-next-line no-console
    console.error('[forms-core] form error:', error, info);
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    const { children } = this.props;
    if (!error) return children;
    const box = {
      margin: '1rem 4rem',
      padding: '1rem',
      border: '2px solid #c12335',
      borderRadius: '4px',
      background: '#fff5f6',
      color: '#2a2c2e',
      fontFamily: 'monospace'
    };
    const pre = { whiteSpace: 'pre-wrap', overflowX: 'auto', fontSize: '12px' };
    return (
      <div style={box}>
        <strong>This form could not be displayed.</strong>
        <p>
          Please try again in a few minutes. If the issue persists, please
          submit an issue under Report an Issue.
        </p>
        {isDev ? (
          <>
            <pre style={pre}>
              {(error && (error.stack || error.message)) || String(error)}
            </pre>
            {info && info.componentStack ? (
              <details>
                <summary>Component stack</summary>
                <pre style={pre}>{info.componentStack}</pre>
              </details>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }
}

FormsCorePreviewBoundary.propTypes = {
  children: PropTypes.node
};
FormsCorePreviewBoundary.defaultProps = {
  children: null
};

export default FormsCorePreviewBoundary;
